import { createI18n } from 'vue-i18n';
import zhCN from './locales/zh-CN';
import enUS from './locales/en-US';

const LOCALE_STORAGE_KEY = 'fap5.locale';
const SUPPORTED_LOCALES = ['zh-CN', 'en-US'] as const;

type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const isSupportedLocale = (locale: string): locale is SupportedLocale => {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale);
};

const normalizeLocale = (locale?: string | null): SupportedLocale | null => {
  if (!locale) {
    return null;
  }

  const trimmed = locale.trim();
  if (isSupportedLocale(trimmed)) {
    return trimmed;
  }

  if (trimmed.toLowerCase().startsWith('zh')) {
    return 'zh-CN';
  }

  if (trimmed.toLowerCase().startsWith('en')) {
    return 'en-US';
  }

  return null;
};

const getLocaleFromBrowser = (): SupportedLocale => {
  const browserLocale = navigator.language?.trim().toLowerCase() || '';
  if (browserLocale.startsWith('zh')) {
    return 'zh-CN';
  }

  return 'en-US';
};

const getInitialLocale = (): SupportedLocale => {
  const cachedLocale = normalizeLocale(localStorage.getItem(LOCALE_STORAGE_KEY));
  if (cachedLocale) {
    return cachedLocale;
  }

  return getLocaleFromBrowser();
};

const initialLocale = getInitialLocale();

export const i18n = createI18n({
  legacy: false,
  locale: initialLocale,
  fallbackLocale: 'zh-CN',
  messages: {
    'zh-CN': zhCN,
    'en-US': enUS,
  },
});

export const setAppLocale = (locale: string) => {
  const nextLocale = normalizeLocale(locale) || 'en-US';
  i18n.global.locale.value = nextLocale;
  localStorage.setItem(LOCALE_STORAGE_KEY, nextLocale);
};

export const getCurrentLocale = () => i18n.global.locale.value;

export { SUPPORTED_LOCALES };
